import React from "react";
import { Box, type ContainerProps } from "@chakra-ui/react";
import { Swiper, type SwiperProps, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
// eslint-disable-next-line import/no-unassigned-import
import "swiper/css";
import { TextTitle } from "ui/text";
import { Container } from "ui/container";
import { SliderNavigationButtons } from "./navigation-buttons";

type SliderProps = {
  id: string;
  slides: JSX.Element[];
  title: string;
  description?: string;
  navigationPr: ContainerProps["pr"];
};

/**
 * A horizontal slider used to display resource cards to the right of a sidebar.
 * Includes a title and navigation buttons that are displayed above the slides.
 */
export function ResourcesSlider({ id, slides, title, description, navigationPr, ...props }: SliderProps & SwiperProps) {
  const previousButtonId = `swiper-navigation-prev-${id}`;
  const nextButtonId = `swiper-navigation-next-${id}`;

  return (
    <Box position="relative" mx="0">
      <Box
        display={{ base: "none", lg: "block" }}
        pos="absolute"
        h="100%"
        w="smallest"
        bgGradient="linear(to right, #fff, 40%, rgba(255, 255, 255, 0))"
        zIndex="2"
      />

      <Container
        display="flex"
        alignItems={{ base: "start", md: title ? "end" : "center" }}
        justifyContent={title ? "space-between" : { base: "center", sm: "end" }}
        flexDir={{ base: "column", md: "row" }}
        gap="small"
        mb={7}
        px="0"
        pr={navigationPr}
        pl={{ base: "smaller", md: "smallest" }}
      >
        {title && (
          <Box flex={1} maxW="xl">
            <TextTitle size="sm" title={title} description={description} />
          </Box>
        )}

        <SliderNavigationButtons
          w="100%"
          justify={{ base: title ? "start" : "center", md: "end" }}
          flex={{ base: 1, md: 0.3 }}
          previousButtonId={previousButtonId}
          nextButtonId={nextButtonId}
        />
      </Container>

      <Swiper
        rewind
        speed={450}
        slidesPerView="auto"
        modules={[Navigation]}
        navigation={{
          prevEl: `#${previousButtonId}`,
          nextEl: `#${nextButtonId}`,
        }}
        style={{ paddingTop: "16px", paddingBottom: "56px", paddingLeft: "12px" }} // Padding keeps hover state and box shadows out of overflow
        {...props}
      >
        {slides.map((slide) => (
          <SwiperSlide key={`${id}-${slide.key}`} style={{ width: "auto", height: "auto" }}>
            {slide}
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}
